/**
 * Internal / external context & scope document: field map + HTML for preview/DOCX export.
 * Mirrors template rows from the engagement CSV (autofetch + standard text blocks).
 */

import {
  normalizeRiskScale,
  getSignificanceThreshold,
  severityCellValue,
  SEVERITY_COLUMNS,
  SEVERITY_CATEGORIES,
} from "../constants/riskAnalysisMatrix";
import { buildGapQuestionnaireScopeFromProject, getProfileAttr } from "./gapQuestionnaireDocument";
import { resolveAdvisorDisplayName } from "./contractParties";

export const CONTEXT_DOCUMENT_TITLE_OPTIONS = [
  "Internal and external context & scope document",
  "Internal / external context & scope document",
];

/** Canonical ISO edition → publication date label (YYYY-MM-DD) for footer reference line. Extend as needed. */
export const ISO_PUBLICATION_DATES_BY_LABEL = Object.freeze({
  "ISO 27001:2022": "2022-10-25",
  "ISO/IEC 27001:2022": "2022-10-25",
  "ISO 27001:2013": "2013-10-01",
});

export const STANDARD_TEXT_INTERESTED_PARTIES = `
Shareholders
Board of Directors
Suppliers
Customers
Regulatory bodies
Customer user groups
Employees of the organization
Contractors providing services to the organization
National or local government organizations
Emergency services
Trade associations and industry bodies
Trade unions
General public
Investors
Distributors
Competitors
Media
Neighbours
Dependents of employees
`.trim();

export const STANDARD_TEXT_PURPOSE_COMPLIANCE_PROGRAM = `
Understand the organization's needs and the necessity for establishing information security management policy and objectives.
Implement and operate controls and measures for managing the organisation's overall capability to manage information security incidents.
Monitor and review the performance and effectiveness of the compliance program.
Continually improve the organization's information security based on objective measurement.
`.trim();

export const STANDARD_TEXT_IMPACT_INCIDENT = `
The impact of any specific incident will obviously depend upon its nature and a comprehensive risk assessment is maintained to assess and mitigate those that can be reasonably identified. In general terms the potential impact of an inability to perform normal business processes will be shown in one or more of the following key areas:
Loss of sales revenue
Risk to life on health and safety grounds
Loss of reputation/customer confidence
Inability to meet our legal obligations
Breach of contractual obligations
Loss of business opportunity
Fines and other penalties
`.trim();

export const STANDARD_TEXT_COMPLIANCE_OBJECTIVES = `
Based on the requirements and issues set out in this document, the following major objectives are set for information security:
Define the main priorities that the compliance program must address, particularly in terms of the potential impacts described in this document.

Objective 1 - Comply with health and safety legislation at all times
Objective 2 - Maintain shareholder confidence
Objective 3 - Maintain customer service levels
Objective 4 - Minimise loss of revenue
Objective 5 - Avoid the loss of PII
`.trim();

export const STANDARD_TEXT_SOA_INTRO = `
Objective of Measurement: Create a Statement of Applicability that contains the necessary controls and justification for inclusions, whether they are implemented or not, and the justification for exclusions of controls from Annex A;
Mechanism of Objective: Applicability of each control reviewed at least once in a year to ensure appropriateness.
`.trim();

/** Boilerplate when onboarding profiling disclaimers apply — aligns loosely with CSV “documents classified…” placeholders */
export const DEFAULT_COPYRIGHT_CLAUSE =
  "This document contains proprietary context compiled during PolicyLens onboarding activities - circulate internally unless labelled otherwise.";

export function escapeHtmlText(raw) {
  return String(raw ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function pickApplicableLines(profileDraft) {
  if (!profileDraft || typeof profileDraft !== "object") return [];
  const a = profileDraft.applicable;
  if (!Array.isArray(a)) return [];
  return a
    .map((row, i) => {
      if (!row || typeof row !== "object") return null;
      const sr = row.sr != null ? String(row.sr) : `${i + 1}`;
      const title = row.title != null ? String(row.title).trim() : "";
      const req = row.reqCount != null ? String(row.reqCount).trim() : "";
      const ctrl = row.controlCount != null ? String(row.controlCount).trim() : "";
      if (!title && !req && !ctrl) return null;
      return [sr, title, req, ctrl].filter(Boolean).join(" - ");
    })
    .filter(Boolean);
}

function bulletsFromIssueCategory(byCategory, cat) {
  const rows = byCategory && typeof byCategory === "object" ? byCategory[cat] : null;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows
    .map((r) => (r && typeof r.issue === "string" ? r.issue.trim() : ""))
    .filter(Boolean);
}

function formatIssueSection(title, lines, fallback) {
  if (lines.length) return `${title}\n${lines.map((l) => `- ${l}`).join("\n")}`;
  return fallback.trim() || "-";
}

function buildRiskAppetiteParagraph(riskOnboarding) {
  const ro = riskOnboarding && typeof riskOnboarding === "object" ? riskOnboarding : {};
  const scale = normalizeRiskScale(ro.riskScale);
  const thresh = ro.significanceThreshold != null ? Number(ro.significanceThreshold) : getSignificanceThreshold(scale);
  const parts = [];
  parts.push(`The organization uses a ${scale}-point impact / severity scale when evaluating information security and compliance risk.`);
  parts.push(`Materiality guideline (significance benchmark product): threshold ${thresh} for this methodology.`);
  const rankBits = [];
  for (const col of SEVERITY_COLUMNS) {
    const v = severityCellValue(scale, col);
    if (v && v !== "NA") rankBits.push(`${col}: rank ${v}`);
    if (rankBits.length >= 6) break;
  }
  if (rankBits.length) parts.push(`Severity mapping (abbrev.): ${rankBits.join("; ")}.`);
  parts.push(
    `Impact categories used in onboarding: ${SEVERITY_CATEGORIES.join(", ")}.`,
  );
  return parts.join(" ");
}

function buildComplianceRegisterText(profile, project, gapWizard) {
  const draft = profile.profilingDocumentDraft && typeof profile.profilingDocumentDraft === "object" ? profile.profilingDocumentDraft : {};
  const lines = [];
  const cert = (
    project?.certification ||
    (Array.isArray(project?.standards) && project.standards[0]) ||
    ""
  ).trim();
  if (cert) lines.push(`Target framework / certification (engagement): ${cert}`);
  if (Array.isArray(project?.standards) && project.standards.length > 1) {
    lines.push(`Additional standards: ${project.standards.slice(1).join("; ")}`);
  }
  const appLines = pickApplicableLines(draft);
  if (appLines.length) {
    lines.push("Profiling - applicable obligations (selected rows):");
    appLines.forEach((x) => lines.push(`- ${x}`));
  } else if (typeof draft.applicable === "string" && draft.applicable.trim()) {
    lines.push(`Profiling snapshot: ${draft.applicable.trim().slice(0, 4000)}`);
  }
  if (gapWizard && typeof gapWizard === "object" && gapWizard.pushedToDashboard === true) {
    const snap = gapWizard.gapDashboardSnapshot;
    if (snap && typeof snap === "object") {
      const rp = snap.readinessPercent != null ? `Dashboard readiness indicator: ${snap.readinessPercent}%` : "";
      if (rp) lines.push(rp);
    } else lines.push("Gap dashboard: data was pushed - see project dashboard for live readiness.");
  }
  if (!lines.length) {
    return "- Complete profiling (applicable requirements) and/or push gap dashboard data for a fuller compliance register summary. -";
  }
  return lines.join("\n");
}

function resolveScopeField(gapDraft, gqScope, scopeKey, warnings, label) {
  const fromReport = gapDraft && typeof gapDraft === "object" ? String(gapDraft[scopeKey] || "").trim() : "";
  if (fromReport) return { value: fromReport, source: "gap_report_draft" };
  const fromQ = gqScope && typeof gqScope === "object" ? String(gqScope[scopeKey] || "").trim() : "";
  if (fromQ) {
    warnings.push(`Scope (${label}) is taken from finalized questionnaire snapshot - gap report draft not yet populated.`);
    return { value: fromQ, source: "gap_questionnaire_document.scope" };
  }
  warnings.push(`Scope (${label}) is missing - finalize questionnaire scope or populate the gap analysis report sections.`);
  return { value: "-", source: "missing" };
}

function buildCompanyOverview(project) {
  const profile = project?.profile || {};
  const draft = profile.profilingDocumentDraft && typeof profile.profilingDocumentDraft === "object" ? profile.profilingDocumentDraft : {};
  const paragraphs = [];

  const legal = getProfileAttr(profile, "legalName");
  const display = (project?.companyName || "").trim();
  const oneLiner =
    legal && display && legal !== display
      ? `Legal name: ${legal}. Common name in this engagement: ${display}.`
      : legal || display
        ? `Organization: ${legal || display}`
        : "";

  if (oneLiner) paragraphs.push(oneLiner);

  const bits = [];
  const add = (label, attrKey) => {
    const v = getProfileAttr(profile, attrKey);
    if (v) bits.push(`${label}: ${v}`);
  };
  add("Industry (GICS)", "gicsIndustry");
  add("Headcount", "companyHeadcount");
  add("Products and services", "productsAndServices");
  add("Lines of business", "linesOfBusiness");
  add("About", "aboutCompany");
  const hq = [getProfileAttr(profile, "hqCity"), getProfileAttr(profile, "hqCountry")].filter(Boolean).join(", ");
  if (hq) bits.push(`HQ: ${hq}`);
  if (bits.length) paragraphs.push(bits.join("\n"));

  if (typeof draft.execSummary === "string" && draft.execSummary.trim()) {
    paragraphs.push(`Executive summary (profiling draft):\n${draft.execSummary.trim().slice(0, 8000)}`);
  }
  if (typeof draft.orgStructure === "string" && draft.orgStructure.trim()) {
    paragraphs.push(`Organization structure (profiling draft):\n${draft.orgStructure.trim().slice(0, 6000)}`);
  }
  if (typeof draft.highLevel === "string" && draft.highLevel.trim()) {
    paragraphs.push(`High-level scope notes:\n${draft.highLevel.trim().slice(0, 4000)}`);
  }

  const out = paragraphs.filter(Boolean).join("\n\n").trim();
  return out || "-";
}

/** "" when the engagement has no certification target: no standard (or its date) is assumed. */
function resolveIsoFooterLine(certification) {
  const cert = String(certification || "").trim();
  if (!cert) return "";
  const date = ISO_PUBLICATION_DATES_BY_LABEL[cert];
  const dated = date ? ` dated ${date}` : "";
  return `ISO reference: ${cert}${dated} (confirm against your contract for the exact edition in force).`;
}

/**
 * Merge user-editable meta from profile with template defaults.
 * @param {Record<string, any>} metaStored profile.internalExternalContextDocumentMeta
 * @param {Record<string, any>} profile
 * @param {Record<string, any>} project
 */
export function buildDefaultInternalExternalContextMeta(profile, project, firm = null) {
  const contract = profile.scopingContractDraft && typeof profile.scopingContractDraft === "object" ? profile.scopingContractDraft : {};
  const legal = getProfileAttr(profile, "legalName") || (project?.companyName || "").trim() || "";
  const advisor = resolveAdvisorDisplayName(contract, firm ?? project?.advisoryFirm);
  const contact = getProfileAttr(profile, "contactName");
  const today = new Date().toISOString().split("T")[0];
  return {
    companyLegalName: legal || "-",
    documentTitle: CONTEXT_DOCUMENT_TITLE_OPTIONS[0],
    reviewFrequency: "Annual review unless superseded by the certification scheme.",
    documentClassification: "Internal",
    disclosureCopyrightClause: DEFAULT_COPYRIGHT_CLAUSE,
    version: "1.0",
    dated: today,
    documentAuthor: advisor,
    documentOwner: contact ? `${contact} / ${legal || "Organization"}`.trim() : legal || "Organization",
    versionHistoryRows: [{ versionNo: "1.0", versionDate: today, revisionAuthor: advisor, summaryOfChanges: "Initial context document generated from engagement data." }],
    distribution: "As defined by the engagement governance process.",
    approvalName: "",
    approvalPosition: "",
    approvalSignature: "",
    approvalDate: "",
    introduction:
      "This document records the internal and external context and the scope inputs for the compliance / information security programme, derived from onboarding and gap analysis artefacts for this engagement.",
    logoNote: "",
  };
}

/**
 * @param {Record<string, any> | undefined} metaStored
 * @param {Record<string, any>} profile
 * @param {Record<string, any>} project
 */
export function mergeInternalExternalContextMeta(metaStored, profile, project) {
  const d = buildDefaultInternalExternalContextMeta(profile, project);
  const m = metaStored && typeof metaStored === "object" && !Array.isArray(metaStored) ? metaStored : {};
  const versionHistoryRows = Array.isArray(m.versionHistoryRows) && m.versionHistoryRows.length ? m.versionHistoryRows : d.versionHistoryRows;
  const storedLegal = typeof m.companyLegalName === "string" && m.companyLegalName.trim() ? m.companyLegalName.trim() : "";
  return { ...d, ...m, versionHistoryRows, companyLegalName: storedLegal || d.companyLegalName };
}

/**
 * @param {Record<string, any>} project
 */
export function gatherInternalExternalContextModel(project) {
  const warnings = [];
  const profile = project?.profile || {};
  const gapWizard = profile.gapAnalysisWizard && typeof profile.gapAnalysisWizard === "object" ? profile.gapAnalysisWizard : {};
  const gapDraft = gapWizard.gapReportDraft && typeof gapWizard.gapReportDraft === "object" ? gapWizard.gapReportDraft : {};
  const gqDoc = profile.gapQuestionnaireDocument && typeof profile.gapQuestionnaireDocument === "object" ? profile.gapQuestionnaireDocument : {};
  const gqScope = gqDoc.scope && typeof gqDoc.scope === "object" ? gqDoc.scope : {};

  const issuesPayload = profile.issuesThreatsOpportunities && typeof profile.issuesThreatsOpportunities === "object" ? profile.issuesThreatsOpportunities : {};
  const byCategory = issuesPayload.byCategory && typeof issuesPayload.byCategory === "object" ? issuesPayload.byCategory : {};

  const internalLines = bulletsFromIssueCategory(byCategory, "Internal Issues");
  const externalLines = bulletsFromIssueCategory(byCategory, "External Issues");
  const rtLines = bulletsFromIssueCategory(byCategory, "Risk and Threats");

  const execSummary = typeof gapDraft.executiveSummary === "string" ? gapDraft.executiveSummary.trim() : "";
  let internalIssuesText = formatIssueSection("Internal Issues", internalLines, "");
  if (!internalLines.length && execSummary) {
    internalIssuesText = `Summary from gap analysis narrative (internal / overall context excerpt):\n${execSummary.slice(0, 6000)}`;
    warnings.push("Internal issues section uses gap report executive summary because no Issues - Internal rows were persisted in onboarding.");
  } else if (!internalLines.length) {
    internalIssuesText = "- Select Internal Issues during Project onboarding or complete the gap report narrative. -";
    warnings.push("Internal issues section is empty (no onboarding issues + no gap report executive summary).");
  }

  let externalIssuesText = formatIssueSection("External Issues", externalLines, "");
  if (externalLines.length === 0 && rtLines.length) {
    externalIssuesText = `${externalIssuesText}\n\nRisk / threat signals (from onboarding):\n${rtLines.map((l) => `- ${l}`).join("\n")}`;
    warnings.push("External issues section supplemented with Risk and Threats list from onboarding.");
  } else if (externalLines.length === 0) {
    externalIssuesText += "\n- No External Issues rows in onboarding - add items or elaborate in subsequent revisions. -";
  }

  const riskOnboarding = profile.riskOnboarding && typeof profile.riskOnboarding === "object" ? profile.riskOnboarding : {};
  if (!riskOnboarding.riskScale) warnings.push("Risk scale not persisted - defaulting narrative to 5-point scale.");

  const scopeEntities = resolveScopeField(gapDraft, gqScope, "legalEntities", warnings, "Entities + Locations");
  const scopeProducts = resolveScopeField(gapDraft, gqScope, "productsServices", warnings, "Products and services");
  const scopeActivities = resolveScopeField(gapDraft, gqScope, "activitiesDepts", warnings, "Activities / Departments");

  if (!gapDraft.executiveSummary && Object.keys(gapDraft).length < 2) {
    warnings.push("Gap report draft is sparse - scope fields fall back to questionnaire snapshot where possible.");
  }

  const certification = project?.certification || (Array.isArray(project?.standards) ? project.standards[0] : "") || "";

  const metaStored = profile.internalExternalContextDocumentMeta;
  const meta = mergeInternalExternalContextMeta(metaStored, profile, project);

  const companyLogoUrl =
    typeof profile.companyLogo === "string" && profile.companyLogo.trim()
      ? profile.companyLogo.trim()
      : typeof meta.logoNote === "string" && meta.logoNote.trim()
        ? meta.logoNote.trim()
        : "";

  const logoPlaceholder =
    companyLogoUrl
      ? ` Client branding logo URL on file (embed manually if needed): ${companyLogoUrl}`
      : " Logo: import from Organizational Setup branding when publishing (DOCX preview shows text placeholder only).";

  return {
    meta: {
      ...meta,
      logoPlaceholder,
    },
    autofetch: {
      companyOverview: buildCompanyOverview(project),
      internalIssues: internalIssuesText,
      externalIssues: externalIssuesText,
      riskAppetite: buildRiskAppetiteParagraph(riskOnboarding),
      complianceRegister: buildComplianceRegisterText(profile, project, gapWizard),
      scopeEntities: scopeEntities.value,
      scopeEntitiesSource: scopeEntities.source,
      scopeProducts: scopeProducts.value,
      scopeProductsSource: scopeProducts.source,
      scopeActivities: scopeActivities.value,
      scopeActivitiesSource: scopeActivities.source,
    },
    standardText: {
      interestedParties: STANDARD_TEXT_INTERESTED_PARTIES,
      purposeComplianceProgram: STANDARD_TEXT_PURPOSE_COMPLIANCE_PROGRAM,
      impactIncident: STANDARD_TEXT_IMPACT_INCIDENT,
      complianceObjectives: STANDARD_TEXT_COMPLIANCE_OBJECTIVES,
      soaIntro: STANDARD_TEXT_SOA_INTRO,
    },
    certification,
    isoReferenceLine: resolveIsoFooterLine(certification),
    warnings,
    /** Frozen questionnaire scope helper for QA */
    questionnaireScopeFallback: buildGapQuestionnaireScopeFromProject(project, project?.advisoryFirm),
  };
}

function renderMetaTable(meta) {
  const rows = [
    ["Company legal name", meta.companyLegalName],
    ["Document title", meta.documentTitle],
    ["Review frequency", meta.reviewFrequency],
    ["Document classification", meta.documentClassification],
    ["Disclosure / copyright", meta.disclosureCopyrightClause],
    ["Version", meta.version],
    ["Dated", meta.dated],
    ["Document author", meta.documentAuthor],
    ["Document owner", meta.documentOwner],
    ["Distribution", meta.distribution],
    ["Approval - Name", meta.approvalName || "-"],
    ["Approval - Position", meta.approvalPosition || "-"],
    ["Approval - Signature", meta.approvalSignature || "-"],
    ["Approval - Date", meta.approvalDate || "-"],
  ];
  const trs = rows
    .map(
      ([k, v]) =>
        `<tr><th style="text-align:left;padding:6px;border:1px solid #ccc;">${escapeHtmlText(k)}</th><td style="padding:6px;border:1px solid #ccc;white-space:pre-wrap;">${escapeHtmlText(v)}</td></tr>`,
    )
    .join("");
  const hist = Array.isArray(meta.versionHistoryRows) ? meta.versionHistoryRows : [];
  const histRows = hist
    .map(
      (h) =>
        `<tr><td style="padding:6px;border:1px solid #ccc;">${escapeHtmlText(h.versionNo)}</td><td style="padding:6px;border:1px solid #ccc;">${escapeHtmlText(h.versionDate)}</td><td style="padding:6px;border:1px solid #ccc;">${escapeHtmlText(h.revisionAuthor)}</td><td style="padding:6px;border:1px solid #ccc;white-space:pre-wrap;">${escapeHtmlText(h.summaryOfChanges)}</td></tr>`,
    )
    .join("");
  const histSection =
    hist.length > 0
      ? `<h3>Version history</h3><table style="border-collapse:collapse;width:100%;"><thead><tr><th style="border:1px solid #ccc;padding:6px;">Ver.</th><th style="border:1px solid #ccc;padding:6px;">Date</th><th style="border:1px solid #ccc;padding:6px;">Author</th><th style="border:1px solid #ccc;padding:6px;">Summary</th></tr></thead><tbody>${histRows}</tbody></table>`
      : "";
  return `<h2>Document control</h2><p style="white-space:pre-wrap;">${escapeHtmlText(meta.logoPlaceholder)}</p><table style="border-collapse:collapse;width:100%;">${trs}</table>${histSection}<h2>Introduction</h2><p style="white-space:pre-wrap;">${escapeHtmlText(meta.introduction)}</p>`;
}

function sectionBlock(heading, body) {
  return `<h2>${escapeHtmlText(heading)}</h2><p style="white-space:pre-wrap;">${escapeHtmlText(body)}</p>`;
}

/**
 * Full HTML document body (wrapped by backend with html/head for DOCX).
 * @param {ReturnType<typeof gatherInternalExternalContextModel>} model
 */
export function buildInternalExternalContextHtmlBody(model) {
  const { meta, autofetch, standardText, isoReferenceLine, certification } = model;
  const parts = [];
  parts.push(renderMetaTable(meta));
  parts.push(sectionBlock("Company overview", autofetch.companyOverview));
  parts.push(sectionBlock("Internal issues", autofetch.internalIssues));
  parts.push(sectionBlock("External issues", autofetch.externalIssues));
  parts.push(sectionBlock("Risk appetite", autofetch.riskAppetite));
  parts.push(sectionBlock("Interested parties (reference list)", standardText.interestedParties));
  parts.push(sectionBlock("Compliance register (summary)", autofetch.complianceRegister));
  parts.push(sectionBlock("Purpose of compliance program", standardText.purposeComplianceProgram));
  parts.push(sectionBlock("Potential impact of an information security incident", standardText.impactIncident));
  parts.push(sectionBlock("Compliance program objectives", standardText.complianceObjectives));
  parts.push(sectionBlock("Scope - Entities and locations", autofetch.scopeEntities));
  parts.push(sectionBlock("Scope - Products and services", autofetch.scopeProducts));
  parts.push(sectionBlock("Scope - Activities / departments / functions / divisions", autofetch.scopeActivities));
  parts.push(sectionBlock("Scope - Activities / departments / functions / divisions (continued)", autofetch.scopeActivities));
  parts.push(
    sectionBlock(`Statement of Applicability (SoA)${certification ? ` - ${certification} context` : ""}`, standardText.soaIntro),
  );
  if (isoReferenceLine) parts.push(`<h2>Reference</h2><p>${escapeHtmlText(isoReferenceLine)}</p>`);
  return parts.join("\n");
}
